<script>
import flatpickr from 'flatpickr';
import Validatable from '@/mixins/Validatable';
import { nanoid } from 'nanoid';
import { cn } from '@/utils/Helpers';

export default {
    mixins: [Validatable],

    props: {
        modelValue: {
            type: [String, Date],
            default: ''
        },
        dateFormat: {
            type: String,
            default: 'Y-m-d'
        },
        range: {
            type: Boolean,
            default: false
        },
        rounded: {
            type: Boolean,
            default: false
        },
        enableTime: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        format: {
            type: String,
            default: 'F j, Y' // Changed default to a more human-readable format
        },
        endTime: {
            type: Object
        },
        minDate: {
            type: [Date, Object, String],
            default: '1900-01'
        },
        maxDate: {
            type: [Date, Object, String],
            default: '2099-12'
        },
        inputClass: {
            type: [String, Array, Object],
        }
    },

    emits: ['update:modelValue', 'change', 'close', 'blur'],

    watch: {
        modelValue(newValue) {
            if (this.flatpickr && newValue) {
                try {
                    this.flatpickr.setDate(newValue, false);
                } catch (error) {
                    console.warn('Invalid date format:', newValue);
                }
            }
        },
        minDate(newValue) {
            if (this.flatpickr) {
                this.flatpickr.set('minDate', newValue);
            }
        },
        maxDate(n, o) {
            if (this.flatpickr) {
                this.flatpickr.set('maxDate', n)
            }
        }
    },

    computed: {
        id () {
            return this.name || nanoid();
        },
    },

    mounted() {
        let self = this;
        this.$nextTick(() => {
            this.flatpickr = flatpickr(this.$refs.dateInput, {
                static: true,
                enableTime: this.enableTime,
                enableSeconds: false,
                altInput: true,
                dateFormat: this.dateFormat, // Format saved to modelValue
                altFormat: this.format, // Format displayed to user
                allowInput: true,
                minDate: this.minDate,
                maxDate: this.maxDate,
                mode: this.range ? 'range' : 'single',
                onChange: (selDates, dateStr) => {
                    if (selDates.length > 0) {
                        const formattedDate = this.flatpickr.formatDate(selDates[0], this.dateFormat);
                        this.update(formattedDate);
                    }

                    if (this.endTime) {
                        this.endTime.flatpickr.set('minDate', selDates[0]);
                    }
                },
                onClose: function (selDates, dateStr, instance) {
                    if (self.flatpickr.selectedDates.length === 0) {
                        self.update(null);
                    }
                    
                    self.form.errors.clear(self.id);
                }
            });

            // Set initial date if provided
            if (this.modelValue) {
                try {
                    this.flatpickr.setDate(this.modelValue, false);
                } catch (error) {
                    console.warn('Invalid initial date format:', this.modelValue);
                }
            }
        })
    },

    data() {
        return {
            flatpickr: null
        };
    },

    methods: {
        cn,
        update(value) {
            this.form.errors.clear(this.id);
            this.$emit('update:modelValue', value);
            this.$emit('change', value);
        },
        clear() {
            this.flatpickr.clear();
            this.update(null);
        }
    },

    destroyed () {
        if (this.flatpickr) {
            this.flatpickr.clear();
        }
    }
}
</script>

<template>
    <div class="form-group__helper is-datepicker" :class="{ 'is-range': range }">
        <label v-if="label" :for="id" class="form-group__label whitespace-nowrap" style="display: block;">{{ label }}</label>
        <span v-if="hint" class="form-group__hint" style="display: block;">{{ hint }}</span>
        <label :for="id" :class="cn('form-group__input-wrapper m-0 flex items-center border border-dust-300 bg-white rounded-md', inputClass)">
            <input
                v-bind="attrs"
                class="form-group__input !bg-transparent pl-4"
                type="text"
                ref="dateInput"
                :class="{ 'is-error': invalid, 'is-rounded': rounded }"
                :id="id"
                :name="id"
                :disabled="disabled"
            >
            <NIcon as="calendar-date-picker" class="ml-1 mt-[2px]" />
        </label>
        <span v-if="invalid" class="form-group__error">{{ error }}</span>
    </div>
</template>

<style lang="scss">
    .flatpickr-calendar.static.open {
        position: absolute !important;
    }
</style>
